import { useAppStore } from '@/store/useAppStore.ts'
import { getTemperatureFactor } from '@brt/shared-logic'
import { getChemistryPreset } from '@brt/shared-logic'
import { TemperatureUnit } from '@brt/shared-types'
import TemperatureDeratingDisplay from '@/components/chemistry/TemperatureDeratingDisplay.tsx'
import { ExpressionInput, RangeSlider, UnitToggle } from 'calc-controls'

const MIN_C = -40
const MAX_C = 70

export function TemperatureInput() {
  const tempC = useAppStore((s) => s.battery.temperatureC)
  const chemistry = useAppStore((s) => s.battery.chemistry)
  const tempUnit = useAppStore((s) => s.preferences.temperatureUnit)
  const updateBattery = useAppStore((s) => s.updateBattery)
  const updatePreference = useAppStore((s) => s.updatePreference)

  const preset = getChemistryPreset(chemistry)
  const factor = getTemperatureFactor(tempC, chemistry)

  const isOutOfRange = tempC < preset.minTempC || tempC > preset.maxTempC
  const isExtreme = tempC <= (preset.minTempC + 10) || tempC >= (preset.maxTempC - 10)

  const displayTemp = tempUnit === TemperatureUnit.FAHRENHEIT
    ? Math.round((tempC * 9 / 5 + 32) * 10) / 10
    : tempC

  const minDisplay = tempUnit === TemperatureUnit.FAHRENHEIT
    ? Math.round(MIN_C * 9 / 5 + 32)
    : MIN_C

  const maxDisplay = tempUnit === TemperatureUnit.FAHRENHEIT
    ? Math.round(MAX_C * 9 / 5 + 32)
    : MAX_C

  const handleValueChange = (val: number) => {
    const celsius = tempUnit === TemperatureUnit.FAHRENHEIT
      ? (val - 32) * 5 / 9
      : val
    const clamped = Math.max(MIN_C, Math.min(MAX_C, celsius))
    updateBattery('temperatureC', Math.round(clamped * 10) / 10)
  }

  return (
    <div className="temperature-input">
      <div className="temperature-input__header">
        <UnitToggle
          label="Temperature"
          options={[
            { label: '°C', value: TemperatureUnit.CELSIUS },
            { label: '°F', value: TemperatureUnit.FAHRENHEIT },
          ]}
          value={tempUnit}
          onChange={(v) => updatePreference('temperatureUnit', v as TemperatureUnit)}
        />
      </div>
      <ExpressionInput
        label={tempUnit === TemperatureUnit.CELSIUS ? 'Temperature (°C)' : 'Temperature (°F)'}
        value={displayTemp}
        onChange={handleValueChange}
        min={minDisplay}
        max={maxDisplay}
        decimals={1}
      />
      <RangeSlider
        label="Temp range"
        value={tempC}
        onChange={(v) => updateBattery('temperatureC', v)}
        min={MIN_C}
        max={MAX_C}
        step={1}
        decimals={0}
      />
      <div id="temp-derating">
        <TemperatureDeratingDisplay factor={factor} chemistry={chemistry} />
      </div>
      {isOutOfRange && (
        <p className="temperature-input__warning temperature-input__warning--danger" role="alert">
          Temperature outside safe range ({preset.minTempC}°C to {preset.maxTempC}°C)
        </p>
      )}
      {!isOutOfRange && isExtreme && (
        <p className="temperature-input__warning temperature-input__warning--caution" role="alert">
          Approaching temperature limit — significant derating applies
        </p>
      )}
    </div>
  )
}
